import React, { useEffect, useState } from "react";
import { Layout, Row, Col } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { FetchLandingPageAction, FetchMaxDateAction } from "../../redux/action/LandingPageAction";
import { getUserClaims } from "../../utils/auth/unified-auth";
import { useAuth } from "../../utils/auth/useAuth";
import { LoadingSpinner, PageHeader, Icon, Button, Switcher, Tooltip, ButtonIcon } from "pepsico-ds";
import { Outlet, useNavigate, useNavigationType } from "react-router-dom";
import CommodityCard from "../../components/Cards/CommodityCard";
import MIInHouseModalNew from "../../components/Modal/MIInHouseModalNew";
import { appNames } from "../../constants/appNames";
import { allCategoryL0 } from "../../mocks/userJson";
import { getRoles, getSectors, getBusinessUnitsBySector, getCategoriesBySectorAndBusinessUnit, getCategoriesLevel1, getCategoriesLevel2, getYears } from "../KPIOverview/KpiUtils";
import Fastmarkets from '../../assets/Fastmarkets.svg'
import Smartcube from '../../assets/Smartcube.svg'
import MinTec from '../../assets/MinTec.svg'
import CommodityPageHeader from "../../components/Header/CommodityPageHeader";
import { localStoreKeys } from '../../constants/localStoreKeys';
import { setKeyValue } from "../../redux/reducer/localDataSlice";
import { FetchCommodityLandingAction } from "../../redux/action/RISIAction";
import { FetchMIL1Action } from "../../redux/action/CommodityPackagingAction";
import CR_Banner from '../../assets/CR_Banner.svg'
import { call7DaysNpsLogic, formatIntoDropdownData } from "../../utils/utils";
import { useLoginEffects } from "../WalkmeTour/UseLoginEffects";
import CustomButtons from "../../components/Cards/CustomButtons";

const defaultYear = window.YEAR

function buildMarketIntelLandingContext(rawUserData, yearDefault) {
    const rolesList = getRoles(rawUserData?.details);
    const matchedRole = rolesList.filter((item) => item.displayText === rawUserData?.defaultRole);
    const roleInfo = rawUserData?.details?.filter((item) => item.role === matchedRole?.[0]?.displayText)?.[0];

    const allYearsList = getYears(rawUserData?.year);
    const yearSelectedValue = yearDefault !== "-"
        ? formatIntoDropdownData([Number(yearDefault)], allYearsList)
        : allYearsList;

    if (!roleInfo?.roleDetail) {
        return { allYearsList, filters: {} };
    }

    const sectorsList = getSectors(roleInfo.roleDetail);
    const sectorsToInclude = sectorsList?.map((item) => item.displayText);

    const businessUnitsList = getBusinessUnitsBySector(roleInfo.roleDetail, sectorsToInclude);
    const businessUnitsToInclude = businessUnitsList?.map((item) => item.displayText);

    const categoriesList = getCategoriesBySectorAndBusinessUnit(roleInfo.roleDetail, sectorsToInclude, businessUnitsToInclude);
    const categoriesToInclude = categoriesList.map((item) => item.displayText);
    const l0Dropdown = formatIntoDropdownData(categoriesToInclude, allCategoryL0);

    const categoriesLevel1List = getCategoriesLevel1(roleInfo.roleDetail, categoriesToInclude, sectorsToInclude, businessUnitsToInclude);
    const categoriesLevel1ToInclude = categoriesLevel1List.map((item) => item.displayText);

    const categoriesLevel2List = getCategoriesLevel2(roleInfo.roleDetail, categoriesToInclude, categoriesLevel1ToInclude, sectorsToInclude, businessUnitsToInclude, "");

    return {
        allYearsList,
        filters: {
            role: matchedRole,
            sectors: sectorsList,
            bus: businessUnitsList,
            l0s: l0Dropdown,
            l1s: categoriesLevel1List,
            l2s: categoriesLevel2List,
            l0sSelected: l0Dropdown,
            l1sSelected: categoriesLevel1List,
            l2sSelected: categoriesLevel2List,
            year: yearSelectedValue,
            allSectors: sectorsList,
            allYears: allYearsList
        }
    };
}

function CommodityOverview() {
    const navigate = useNavigate();
    const navigationType = useNavigationType();
	const dispatch = useDispatch();
	const { authState } = useAuth();
	const [userInfo, setUserInfo] = useState({});
    const [isDataLoading, setIsDataLoading] = useState(false);
    const [isUserDataFetched, setIsUserDataFetched] = useState(false)
    const [openMIInHouseModal, setOpenMIInHouseModal] = useState(false)
    const [miYearsList, setMiYearsList] = useState([])
    const [miLandingPageFilters, setMiLandingPageFilters] = useState({})
	
	useEffect(() => {
		async function fetchUserInfo() {
			const user = await getUserClaims();
			if (user) {
				localStorage.setItem('userInfoEmailID', btoa(JSON.stringify(user?.useremail)));
				setUserInfo(user);
				dispatch(FetchLandingPageAction(user));
				dispatch(FetchMaxDateAction(user));
				dispatch(setKeyValue({ key: localStoreKeys.oktaUserData, value: user }));
				setIsUserDataFetched(true);
			}
		}
        setIsDataLoading(true)
		fetchUserInfo();
		// dispatch(FetchMIL1Action(userData?.emailId));
	}, []);
    
	const userData = useSelector((state) => state.getLandingPageReducer);
	const cardData = useSelector((state) => state.getCommodityLandingReducer);
	const miL1Data = useSelector((state) => state.getMIL1Reducer);
    const userAccessData = useSelector((state) => state.getUserAccessReducer);
    const oktaUserData = useSelector((state) => state.localData?.data?.oktaUserData);
    const maxDateData = useSelector((state) => state.getMaxDateReducer);
    const risiData = cardData?.risiData
    
    const isAIUser = userAccessData?.isAiUser;

    const allCategoryCards = miL1Data?.cardDetail || [];
    const persistedSwitcherIndex = useSelector((state) => state.localData?.data?.[localStoreKeys.commodityL1SwitcherIndex]);
    const shouldRestoreSwitcher = useSelector((state) => state.localData?.data?.[localStoreKeys.commodityL1RestoreSwitcher]);
    // Only restore the previously selected tab when returning via back/forward (POP) navigation, or via the L2 breadcrumb link back to this page; any other fresh (PUSH) visit defaults to 'All'.
    const [switcherIndex, setSwitcherIndex] = useState((navigationType === "POP" || shouldRestoreSwitcher) ? (persistedSwitcherIndex ?? 0) : 0);
    const [directsCards, setDirectsCards] = useState([]);
    const [indirectsCards, setIndirectsCards] = useState([]);
    const [coManCards, setCoManCards] = useState([]);
    useLoginEffects(userData)

    useEffect(() => {
        // Clear stale persisted selection/flag on a fresh (non-restoring) visit so it doesn't leak into a later navigation.
        if (navigationType !== "POP" && !shouldRestoreSwitcher && persistedSwitcherIndex) {
            dispatch(setKeyValue({ key: localStoreKeys.commodityL1SwitcherIndex, value: 0 }));
        }
        if (shouldRestoreSwitcher) {
            dispatch(setKeyValue({ key: localStoreKeys.commodityL1RestoreSwitcher, value: false }));
        }
    }, []);

    useEffect(() => {
        setDirectsCards(allCategoryCards.filter((card) => card.l0Category === "DIRECTS"));
        setIndirectsCards(allCategoryCards.filter((card) => card.l0Category === "INDIRECTS"));
        setCoManCards(allCategoryCards.filter((card) => card.l0Category === "CO MAN"));
    }, [miL1Data]);

    // index 0: All, 1: Directs, 2: Indirects, 3: CoMan - keeps the original cardDetail order within each category
    const switcherCardsMap = [allCategoryCards, directsCards, indirectsCards, coManCards];
    const displayedCards = switcherCardsMap[switcherIndex] || allCategoryCards;

    /* istanbul ignore next */
    useEffect(() => {
        if (maxDateData && maxDateData.type === "SUCCESS_MAX_DATE_ACTION") {
            const maxDateDataArray = Object.keys(maxDateData)
                .filter(key => key !== "type")
                .map(key => maxDateData[key]);
            dispatch(setKeyValue({ key: localStoreKeys.maxDateForApps, value: maxDateDataArray }))
        }
    }, [maxDateData])

    /* istanbul ignore next */
    useEffect(() => {
        if (userData?.type === "SUCCESS_LANDING_PAGE_ACTION") {
            const { allYearsList, filters } = buildMarketIntelLandingContext(userData, defaultYear);
            setMiYearsList(allYearsList);
            setMiLandingPageFilters(filters);

            localStorage.setItem("maxYear", btoa(allYearsList[0]?.displayText));
            localStorage.setItem("minYear", btoa(allYearsList[allYearsList.length - 1]?.displayText));
            localStorage.setItem("yearSelected", filters?.year?.[0]?.displayText);
        }
    }, [userData])

    /* istanbul ignore next */
    useEffect(() => {
        if (cardData && cardData?.type === "SUCCESS_COMMODITY_MAIN_ACTION") {

            setIsDataLoading(false)
        
        }
    }, [cardData])

    useEffect(() => {
        return () => {
            dispatch({ 
                type: 'RESET_COMMODITY_MAIN_ACTION', 
                payload: "RESET_COMMODITY_MAIN_ACTION" 
            });
        };
    }, []);
	
    /* istanbul ignore next */
    useEffect(() => {
        if (isUserDataFetched && userData && userData.type === "SUCCESS_LANDING_PAGE_ACTION") {

            // dispatch(setKeyValue({ key: localStoreKeys.isNPSSubmittedRecently, value: false }))
            // // 7 days NPS logic
            // const now = Date.now(); // current timestamp in milliseconds

            // const responseDate = new Date(userData?.mostRecentDate).getTime(); // convert to timestamp in ms

            // const diffInMs = Math.abs(now - responseDate); // difference in milliseconds

            // const daysDiff = diffInMs / (1000 * 60 * 60 * 24); // convert ms to days

            // if (daysDiff >= 7) {
            //     dispatch(setKeyValue({ key: localStoreKeys.isNPSSubmittedRecently, value: false }))
            // } else {
            //     dispatch(setKeyValue({ key: localStoreKeys.isNPSSubmittedRecently, value: true }))
            // }

            call7DaysNpsLogic(dispatch, userData)
            setIsDataLoading(true)
            
            dispatch(FetchCommodityLandingAction());
            dispatch(FetchMIL1Action(userData?.emailId));
        }
		
    }, [userData])

	if(authState?.isAuthenticated){
		return(
			
                <>
                    <CommodityPageHeader header={"Market Intelligence Hub"}
                        subtext={"Explore Commodity Prices, Forecasts, Market Reports, and Insights from various Market Intelligence sources to translate market signals into decisive Procurement actions."}
                        image={CR_Banner}/>

                    {
                        isAIUser && 
                        <div className="notification info" data-testid="notification">
                            <div className="notification-icon" >
                                <Icon icon="auto_awesome" style={{ color:'#0755AA' }} />
                            </div>    
                            <div className="notification-content">
                                <div className="notification-text">
                                    <span
                                        data-testid="notification-title"
                                        className="font font-typography $color-text-primary $font-heading-h6-bold"
                                        >
                                            Market Intelligence AI
                                    </span>
                                    <span
                                        data-testid="notification-text"
                                        className="font font-typography $color-text-primary $font-small-regular"
                                        >
                                            Powered by Fastmarkets RISI, access market pricing, forecasts, and industry intelligence to support sourcing decisions.
                                    </span>
                                </div>
                                <div className="notification-actions">
                                    <Button
                                        className=""
                                        iconTrailing={"arrow_forward"}
                                        size="medium"
                                        style={{}}
                                        text={"Access Agent"}
                                        variant="secondary"
                                        onClick={function clickButton() {
                                            navigate("/ai-assistant/market-intelligence");
                                        }}
                                    />
                                </div>
                            </div>
                        </div>

                    }
                    

                    <div className="commodity-detail-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: "18px", lineHeight:"56px"}}>
                        <span>Browse by Procurement Category</span>
                        
                    </div>

                    <div className="commodity-detail-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px", fontSize: "18px", lineHeight:"56px" }}>
                       
                        <Switcher
                            className=""
                            items={[
                                {
                                label: 'All',
                                leadingIcon: 'edit',
                                showLeadingIcon: false
                                },
                                {
                                label: 'Directs',
                                leadingIcon: 'edit',
                                showLeadingIcon: false
                                },
                                {
                                label: 'Indirects',
                                leadingIcon: 'edit',
                                showLeadingIcon: false
                                },
                                {
                                label: 'CoMan',
                                leadingIcon: 'edit',
                                showLeadingIcon: false
                                }
                            ]}
                            selectedItemIndex={switcherIndex}
                            onUpdate={(val) => {
                                setSwitcherIndex(val);
                                dispatch(setKeyValue({ key: localStoreKeys.commodityL1SwitcherIndex, value: val }));
                            }}
                            style={{}}
                        /> 
                        <span style={{display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                            {
                            (isDataLoading || cardData?.type !== "SUCCESS_COMMODITY_MAIN_ACTION") ?
                            <></>
                            :
                            <Tooltip direction="left" 
                                text={"Request and explore tailored market insights crafted by our in-house MI experts."}>
                                {/* <Icon alt="" icon="info" size="medium" style={{ marginRight: "10px", marginBottom: "10px", color: "#004895" }}/> */}
                                <ButtonIcon
                                    icon="bar_chart"
                                    style={{margin: "0 10px 10px 0"}}
                                    label=""
                                    onClick={() => setOpenMIInHouseModal(true)}
                                    size="medium"
                                    type="default" />
                            </Tooltip>
                            }
                            <CustomButtons 
                                appList={miL1Data?.apps} 
                            />
                        </span>
                    </div>
                    {
                        
                    (isDataLoading || cardData?.type !== "SUCCESS_COMMODITY_MAIN_ACTION") ? 
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: 300 }}>
                        <LoadingSpinner size={{ height: '80px', width: '80px' }} />
                    </div>
                    :
                    <>
                    
					<Row gutter={[20, 20]}>
                        {/* <Col xs={15} sm={15} md={12} lg={12} xl={8}>
                            <CommodityCard card= {{name:"Packaging",
                            description: null, 
                            image: null,
                            subDescription: null,
                            buttonText: "Launch",
                            url:"/overview/commodity/packaging-indices",
                            data: null
                            }}
                            subCategories={"Corrugated | PET Resin"}
                            sources={"FASTMARKETS (RISI) | SBACCI | CMA | Woodmac"}
                            capabilities={"Commodity Prices, Forecasts, Market Reports"}/>
                        </Col>
                        <Col xs={15} sm={15} md={12} lg={12} xl={8}>
                            <CommodityCard card= {{name:"Oils & Grains", 
                            description: "Stay tuned for updates", 
                            image: null, //Smartcube, 
                            subDescription: null, // "Coming Soon",
                             buttonText: "Coming Soon",
                             url:null,
                             data: null}}/>
                        </Col>
                        <Col xs={15} sm={15} md={12} lg={12} xl={8}>
                            <CommodityCard card= {{name:"Ingredient & Sweeteners", 
                            description: "Stay tuned for updates", 
                            image: null, //MinTec, 
                            subDescription: null, // "Coming Soon", 
                            buttonText: "Coming Soon",
                            url:null,
                            data: null}}/>
                        </Col> */}
                        {displayedCards.map((card, index) => (
                            <Col key={index} xs={15} sm={15} md={12} lg={12} xl={8}>
                                <CommodityCard
                                    card={{
                                        name: card.name,
                                        description: card.description,
                                        image: card.image,
                                        subDescription: card.subDescription,
                                        buttonText: card.buttonText,
                                        url: card.url,
                                        data: card.data,
                                        l0Category: card.l0Category,
                                        count: card.count,
                                        isFavorite: card.isFavorite,
                                        dataAsOn: card.dataAsOn,
                                        refreshFrequency: card.refreshFrequency,                                        
                                    }}
                                    subCategories={card.subCategory}
                                    sources={card.sources}
                                    capabilities={card.capabilities}
                                    isShowPin={true}
                                    isL1Page={true}
                                />
                            </Col>
                        ))}
                    </Row>
                    </>
                    }
                    {
                        openMIInHouseModal &&
                        <MIInHouseModalNew
                            openModal={openMIInHouseModal}
                            setOpenModal={setOpenMIInHouseModal}
                            userData={oktaUserData}
                            capability={appNames.marketIntel}
                            selectedYear={miYearsList}
                            userDataLandingPage={userData}
                            landingPageFilters={miLandingPageFilters}
                            appName={"In-house MI Team"}
                        />
                    }
                </>
		)
	}
}

export default CommodityOverview;